/**
 * How the admin Nova usage table sorts and which columns it shows, both kept
 * in the page address. Coach is the row label: it sorts but never hides.
 *
 * Aliases: nova usage sort, nova usage columns, admin nova usage table state,
 * usage table search params
 */

import { z } from "zod";

import {
  SORT_DIRECTIONS,
  type SortDefaults,
  type SortDirection,
} from "~/lib/shared/sort";

export const NOVA_USAGE_SORTS = [
  "coach",
  "conversations",
  "runs",
  "runCost",
  "input",
  "cacheRead",
  "cacheWrite",
  "output",
  "cost",
  "updated",
] as const;
export type NovaUsageSort = (typeof NOVA_USAGE_SORTS)[number];

/** Every column an admin can hide, in table order. Models does not sort. */
export const NOVA_USAGE_COLUMN_IDS = [
  "conversations",
  "runs",
  "runCost",
  "input",
  "cacheRead",
  "cacheWrite",
  "output",
  "cost",
  "models",
  "updated",
] as const;
export type NovaUsageColumnId = (typeof NOVA_USAGE_COLUMN_IDS)[number];

// A stale or hand-edited link drops the params it gets wrong instead of
// failing the page: `?sort=nonsense` shows the default cost order.
export const novaUsageSearchSchema = z.object({
  sort: z.enum(NOVA_USAGE_SORTS).optional().catch(undefined),
  dir: z.enum(SORT_DIRECTIONS).optional().catch(undefined),
  cols: z.array(z.enum(NOVA_USAGE_COLUMN_IDS)).optional().catch(undefined),
});
export type NovaUsageSearch = z.infer<typeof novaUsageSearchSchema>;

/** The page starts with the most expensive coach; names read A-Z, every figure highest first. */
export const NOVA_USAGE_SORT_DEFAULTS: SortDefaults<NovaUsageSort> = {
  sort: "cost",
  direction: (sort) => (sort === "coach" ? "asc" : "desc"),
};

export interface NovaUsageTableState {
  sort: NovaUsageSort;
  direction: SortDirection;
  /** The shown columns in table order. */
  columns: readonly NovaUsageColumnId[];
}

/**
 * The sort and columns the address asks for. Without `cols` every column
 * shows. A sort on a hidden column returns to the default, so the table never
 * orders by something the viewer cannot see.
 */
export function resolveNovaUsageTableState(
  search: NovaUsageSearch,
): NovaUsageTableState {
  const columns =
    search.cols === undefined
      ? NOVA_USAGE_COLUMN_IDS
      : NOVA_USAGE_COLUMN_IDS.filter((id) => search.cols?.includes(id));
  const requested = search.sort ?? NOVA_USAGE_SORT_DEFAULTS.sort;
  const isHidden =
    requested !== "coach" && !columns.some((id) => id === requested);
  const sort = isHidden ? NOVA_USAGE_SORT_DEFAULTS.sort : requested;
  return {
    sort,
    direction:
      isHidden || search.dir === undefined
        ? NOVA_USAGE_SORT_DEFAULTS.direction(sort)
        : search.dir,
    columns,
  };
}

/**
 * The address change after one column is shown or hidden. All columns shown
 * drops `cols`. Hiding the sorted column also drops the sort, so the link
 * reads as the default again.
 */
export function toggleNovaUsageColumn(
  state: NovaUsageTableState,
  column: NovaUsageColumnId,
): {
  cols: NovaUsageColumnId[] | undefined;
  sort?: undefined;
  dir?: undefined;
} {
  const isShown = state.columns.includes(column);
  const next = NOVA_USAGE_COLUMN_IDS.filter((id) =>
    id === column ? !isShown : state.columns.includes(id),
  );
  const cols = next.length === NOVA_USAGE_COLUMN_IDS.length ? undefined : next;
  return isShown && column === state.sort
    ? { cols, sort: undefined, dir: undefined }
    : { cols };
}

interface NovaUsageSortFields {
  coachName: string;
  conversationCount: number;
  runCount: number;
  runCostUsd: number;
  inputTokens: number;
  cacheReadInputTokens: number;
  cacheWriteInputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  updatedAt: Date | string | null;
}

// One fixed locale so the server render and every browser agree; base
// sensitivity ignores accents and capitals the way the alphabet does.
const nameCollator = new Intl.Collator("en", { sensitivity: "base" });

function compareNames(
  left: NovaUsageSortFields,
  right: NovaUsageSortFields,
): number {
  return nameCollator.compare(left.coachName, right.coachName);
}

/** A coach without activity ("Never") counts as the oldest. */
function updatedTime(row: NovaUsageSortFields): number {
  return row.updatedAt === null ? 0 : new Date(row.updatedAt).getTime();
}

function compareBy(
  sort: Exclude<NovaUsageSort, "coach">,
  left: NovaUsageSortFields,
  right: NovaUsageSortFields,
): number {
  switch (sort) {
    case "conversations":
      return left.conversationCount - right.conversationCount;
    case "runs":
      return left.runCount - right.runCount;
    case "runCost":
      return left.runCostUsd - right.runCostUsd;
    case "input":
      return left.inputTokens - right.inputTokens;
    case "cacheRead":
      return left.cacheReadInputTokens - right.cacheReadInputTokens;
    case "cacheWrite":
      return left.cacheWriteInputTokens - right.cacheWriteInputTokens;
    case "output":
      return left.outputTokens - right.outputTokens;
    case "cost":
      return left.estimatedCostUsd - right.estimatedCostUsd;
    case "updated":
      return updatedTime(left) - updatedTime(right);
    default: {
      const exhaustive: never = sort;
      throw new Error(`Unhandled Nova usage sort: ${String(exhaustive)}`);
    }
  }
}

/**
 * The rows in the requested order, as a new array. Equal figures fall back to
 * the coach name, which always reads A-Z.
 */
export function sortNovaUsageCoaches<Row extends NovaUsageSortFields>(
  rows: readonly Row[],
  sort: NovaUsageSort,
  direction: SortDirection,
): Row[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) =>
    sort === "coach"
      ? sign * compareNames(left, right)
      : sign * compareBy(sort, left, right) || compareNames(left, right),
  );
}
